import { FC, memo, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'app/providers/Store';
import { userSelectors, updateUser, getUserById } from 'entities/user';
import { UserRequestDto } from 'shared/api';
import { Card } from 'shared/ui/Card';
import { Button } from 'shared/ui/Button';
import {
  EditOutlined, UserOutlined, MailOutlined, EnvironmentOutlined, PhoneOutlined,
} from '@ant-design/icons';
import { App } from 'antd';
import { useGlobalSpinner } from 'shared/hooks';
import { EditProfileModal } from '../components';
import styles from './ProfilePage.module.scss';

export const ProfilePage: FC = memo(() => {
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const user = useAppSelector(userSelectors.getUser);
  const isLoading = useAppSelector(userSelectors.getIsLoading);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleSave = async (data: UserRequestDto) => {
    if (!user?.id) return;

    try {
      showSpinner('Обновление профиля...');
      await dispatch(updateUser({ id: user.id, data: { ...data, keycloakUserId: user.keycloakUserId } })).unwrap();
      message.success('Профиль успешно обновлен');
      setIsEditModalOpen(false);
      await dispatch(getUserById(user.id)).unwrap();
    } catch (error) {
      message.error('Ошибка при обновлении профиля');
    } finally {
      hideSpinner();
    }
  };

  if (!user) {
    return (
      <div className={styles.profilePage}>
        <Card className={styles.card}>
          <div className={styles.emptyState}>
            <UserOutlined className={styles.emptyIcon} />
            <p>Информация о пользователе не найдена</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.profilePage}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Профиль пользователя</h1>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
            loading={isLoading}
          >
            Редактировать
          </Button>
        </div>

        <div className={styles.content}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Личная информация</h2>
            <div className={styles.field}>
              <span className={styles.label}>Имя:</span>
              <span className={styles.value}>{user.firstname || 'Не указано'}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Фамилия:</span>
              <span className={styles.value}>{user.lastname || 'Не указано'}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Отчество:</span>
              <span className={styles.value}>{user.middleName || 'Не указано'}</span>
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Контактная информация</h2>
            <div className={styles.field}>
              <MailOutlined className={styles.fieldIcon} />
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{user.email || 'Не указано'}</span>
            </div>
            <div className={styles.field}>
              <PhoneOutlined className={styles.fieldIcon} />
              <span className={styles.label}>Телефон:</span>
              <span className={styles.value}>{user.mobilePhone || 'Не указано'}</span>
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Дополнительная информация</h2>
            <div className={styles.field}>
              <EnvironmentOutlined className={styles.fieldIcon} />
              <span className={styles.label}>Компания:</span>
              <span className={styles.value}>{user.company || 'Не указано'}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>ИНН компании:</span>
              <span className={styles.value}>{user.innCompany || 'Не указано'}</span>
            </div>
          </div>
        </div>
      </Card>

      <EditProfileModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSave}
        user={user}
      />
    </div>
  );
});

ProfilePage.displayName = 'ProfilePage';
